import { fetchList } from "../apis/services/CommonApiService";
import { ApiEndPoints } from "../apis/ApiEndPoints";

// ------------------------------------------------------------------
// Revenue By Source
// GET /report/revenue-by-source?start_date=&end_date=
// Returns rows ready to pass straight to PieChartView (selectedTab=2).
// Data shape is reproduced from RevenueBySourceList.js.
// ------------------------------------------------------------------
export async function fetchRevenueBySource({ startDate, endDate }) {
  const result = await fetchList(
    `${ApiEndPoints.GET_REVENUE_BY_SOURCE}?start_date=${startDate}&end_date=${endDate}`
  );
  if (result?.status !== 200) throw new Error("Failed to fetch Revenue By Source");

  const rows = (result.data || []).map((item, i) => ({
    ...item,
    id: i,
    source:
      item.source === 1
        ? "Admin"
        : item.source === 2
        ? "App"
        : item.source === "OTA"
        ? "OTA - " + item.ota_source
        : item.source === 3
        ? "OTA"
        : "Web",
    revenueGenerated: "$ " + Number(item.revenueGenerated).toFixed(2),
    taxAmount: "$ " + Number(item.taxAmount).toFixed(2),
  }));

  const columns = [
    { field: "source", headerName: "Source Type", flex: 1 },
    { field: "numberOfBookings", headerName: "Number of Rooms", flex: 1 },
    { field: "revenueGenerated", headerName: "Revenue Generated", flex: 1 },
    { field: "taxAmount", headerName: "Tax Amount", flex: 1 },
  ];

  const summary = {
    totalBookings: rows.reduce((s, r) => s + +r.numberOfBookings, 0),
    totalRevenue: rows.reduce((s, r) => s + Number(String(r.revenueGenerated).replace(/[^0-9.-]/g, "")), 0),
    totalTax: rows.reduce((s, r) => s + Number(String(r.taxAmount).replace(/[^0-9.-]/g, "")), 0),
  };

  return { rows, columns, summary, selectedTab: 2 };
}

// ------------------------------------------------------------------
// Transaction Batch Report
// GET /report/transaction-batch?start_date=&end_date=
// actualRecords[0] = header row (field → label map),
// the rest are data rows. Shaped from transactionBatchReport.js.
// ------------------------------------------------------------------
export async function fetchTransactionBatch({ startDate, endDate }) {
  const result = await fetchList(
    `${ApiEndPoints.GET_TRANSACTION_BATCH_REPORT}?start_date=${startDate}&end_date=${endDate}`
  );
  if (result?.status !== 200) throw new Error("Failed to fetch Transaction Batch");

  const records = result.data?.actualRecords || [];
  if (records.length < 2) return { rows: [], columns: [], summary: null, selectedTab: 4 };

  // First record is the header row
  const headerRow = records[0];
  const columns = Object.keys(headerRow).map((k) => ({
    field: k,
    headerName: headerRow[k],
    flex: 1,
  }));
  const dataRows = records.slice(1).map((r, i) => ({ ...r, id: i }));
  // Last record is the "Total" row. Turn it into labelled chips using the
  // real column headers (skip the first column, which just says "Total").
  const totalRow = records[records.length - 1];
  const summary = columns
    .slice(1)
    .map((c) => ({ label: c.headerName, raw: totalRow[c.field] }))
    .filter((x) => x.raw !== "" && x.raw != null && !Number.isNaN(Number(x.raw)))
    .slice(0, 3)
    .map((x) => ({ label: x.label, value: `$${Number(x.raw).toFixed(2)}` }));

  return { rows: dataRows, columns, summary, selectedTab: 4 };
}

// ------------------------------------------------------------------
// OTA Commission Report
// GET /report/ota-commission?start_date=&end_date=
// Shaped from CommissionBySourceList.js.
// ------------------------------------------------------------------
export async function fetchOtaCommission({ startDate, endDate }) {
  const result = await fetchList(
    `${ApiEndPoints.GET_OTA_COMMISION_REPORT}?start_date=${startDate}&end_date=${endDate}`
  );
  if (result?.status !== 200) throw new Error("Failed to fetch OTA Commission");

  const rows = (result.data || []).map((item, i) => ({
    ...item,
    id: i,
    source: item.ota_source,
    total_commission: "$ " + Number(item.total_commission).toFixed(2),
  }));

  const columns = [
    { field: "source", headerName: "Source Type", flex: 1 },
    { field: "total_bookings", headerName: "Bookings", flex: 1 },
    { field: "total_commission", headerName: "Total Commission", flex: 1 },
  ];

  const summary =
    rows.length > 0
      ? {
          totalBookings: rows.reduce((s, r) => s + +r.total_bookings, 0),
          totalCommission: rows.reduce(
            (s, r) => s + Number(String(r.total_commission).replace(/[^0-9.-]/g, "")),
            0
          ),
        }
      : null;

  return { rows, columns, summary, selectedTab: 5 };
}